
import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Domain } from "@/types/domain";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { FileText, ExternalLink, ChevronDown, ChevronUp, Image } from "lucide-react";
import { platformTechKnowledgeBases, tokenizedEcosystemKnowledgeBases } from "@/utils/knowledgeBases";
import { useTranslation } from "@/hooks/useTranslation";

interface DomainElementsProps {
  domains: Domain[];
}

const getDomainKnowledgeBases = (symbol: string, t: (key: string) => string) => {
  switch(symbol) {
    case "PlTc":
      return platformTechKnowledgeBases.map(kb => ({
        title: kb.titleKey ? t(kb.titleKey) : kb.title,
        url: kb.url
      }));
    case "TkEc":
      return tokenizedEcosystemKnowledgeBases.map(kb => ({
        title: kb.titleKey ? t(kb.titleKey) : kb.title,
        url: kb.url
      }));
    case "KbCr":
      return [
        { title: t("kb.knowledgeCuration.bestPractices"), url: "#" },
        { title: t("kb.knowledgeCuration.metadataStandards"), url: "#" },
        { title: t("kb.knowledgeCuration.contentDevelopment"), url: "#" }
      ];
    case "KbCo":
      return [
        { title: t("kb.knowledgeComposition.integration"), url: "#" },
        { title: t("kb.knowledgeComposition.assembly"), url: "#" },
        { title: t("kb.knowledgeComposition.alignment"), url: "#" }
      ];
    default:
      return [
        { title: t("kb.sample.one"), url: "#" },
        { title: t("kb.sample.two"), url: "#" },
        { title: t("kb.sample.three"), url: "#" }
      ];
  }
};

const DomainElements: FC<DomainElementsProps> = ({ domains }) => {
  const navigate = useNavigate();
  const [expandedDomain, setExpandedDomain] = useState<string | null>(null);
  const { t } = useTranslation();

  const handleDomainClick = (domain: Domain) => {
    if (expandedDomain === domain.symbol) {
      setExpandedDomain(null);
    } else {
      setExpandedDomain(domain.symbol);
    }

    if (domain.symbol === "PlTc" && expandedDomain !== "PlTc") {
      navigate("/platform-tech");
    }
  };

  const handleKnowledgeBaseClick = (url: string, e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(url, "_blank");
  };

  const handlePdfLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open("file:///C:/Users/integ/OneDrive%20-%20ispai.io/iSPAI/iSPAI%20Corporate/iSPAI%20Content/iSPAI%20Knowledge%20Base%20NFT%20Assignment%20Agreement.pdf", "_blank");
  };

  const handleSolutionBountyPdfClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open("file:///C:/Users/integ/OneDrive%20-%20ispai.io/iSPAI/iSPAI%20Corporate/iSPAI%20Content/iSPAI%20Solution%20Bounty%20NFT%20Assignment%20Agreement_Custom.pdf", "_blank");
  };

  const isTokenizedEcosystem = (symbol: string) => symbol === "Tokenized Ecosystems";

  return (
    <div className="grid grid-cols-5 gap-2">
      {domains.map((domain) => (
        <div key={domain.symbol} className="flex flex-col">
          {isTokenizedEcosystem(domain.symbol) ? (
            <HoverCard openDelay={200} closeDelay={200}>
              <HoverCardTrigger asChild>
                <div 
                  className="bg-primary text-primary-foreground p-4 rounded-lg cursor-pointer transition-all duration-200 hover:scale-105 hover:bg-black hover:shadow-lg flex flex-col items-center relative"
                  onClick={() => handleDomainClick(domain)}
                >
                  <div className="text-sm text-center">{domain.name}</div>
                  <div className="absolute right-2 top-2">
                    {expandedDomain === domain.symbol ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </div>
                </div>
              </HoverCardTrigger>
              <HoverCardContent className="w-auto max-w-lg p-0 border-none shadow-xl">
                <div className="grid grid-cols-1 gap-2">
                  {tokenizedEcosystemKnowledgeBases.map((kb, index) => (
                    <div 
                      key={index}
                      className="bg-white border border-gray-300 rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                    >
                      <div className="p-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
                        <h3 
                          className="text-sm font-medium cursor-pointer hover:underline"
                          onClick={index === 0 ? handlePdfLinkClick : handleSolutionBountyPdfClick}
                        >
                          {t(index === 0 ? "kb.tokenizedEco.knowledgeNft" : "kb.tokenizedEco.solutionBounty")}
                        </h3>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(kb.url, "_blank");
                          }}
                          className="bg-primary text-white p-1 rounded-full hover:bg-primary/80"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </button>
                      </div>
                      <div className="p-2">
                        <img 
                          src={kb.url} 
                          alt={kb.title} 
                          className="w-full h-48 object-contain cursor-pointer" 
                          onClick={(e) => handleKnowledgeBaseClick(kb.url, e)}
                        />
                        <p 
                          className="text-xs text-gray-500 mt-1 cursor-pointer hover:underline" 
                          onClick={index === 0 ? handlePdfLinkClick : handleSolutionBountyPdfClick}
                        >
                          {kb.description} <span className="text-primary">(View full document)</span>
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </HoverCardContent>
            </HoverCard>
          ) : (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div 
                    className="bg-primary text-primary-foreground p-4 rounded-lg cursor-pointer transition-all duration-200 hover:scale-105 hover:bg-black hover:shadow-lg flex flex-col items-center relative"
                    onClick={() => handleDomainClick(domain)}
                  >
                    <div className="text-sm text-center">{domain.name}</div>
                    <div className="absolute right-2 top-2">
                      {expandedDomain === domain.symbol ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent 
                  side="right"
                  className="bg-white text-black border border-gray-200 max-w-[400px] whitespace-pre-line"
                >
                  <p className="font-semibold mb-2">{domain.name}</p>
                  <p className="text-sm">{t("domain.representativeKnowledgeBases")}</p>
                  <p className="text-xs mt-1">{domain.categories}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          
          {expandedDomain === domain.symbol && (
            <div className="mt-2 bg-white rounded-lg shadow-md p-3 border border-gray-200 z-10">
              <h3 className="text-sm font-semibold mb-2">{t("domain.knowledgeBases")}</h3>
              <div className="space-y-2">
                {getDomainKnowledgeBases(domain.symbol, t).map((kb, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer text-xs"
                    onClick={(e) => handleKnowledgeBaseClick(kb.url, e)}
                  >
                    {kb.url.includes('.png') ? (
                      <Image className="h-3 w-3 text-primary flex-shrink-0" />
                    ) : (
                      <FileText className="h-3 w-3 text-primary flex-shrink-0" />
                    )}
                    <span className="flex-1">{kb.title}</span>
                    <ExternalLink className="h-3 w-3 text-gray-400 flex-shrink-0" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default DomainElements;
